// client/src/hooks/useCognitive.js
// TTS + STT hook for Prof. iX chatbot.
// Uses plain useState — no GlobalContext, no reducer, no dispatch needed.

import { useState, useCallback, useRef, useEffect } from 'react';
import cognitiveAPI from '../api/cognitiveAPI';

const MAX_RECORDING_SECONDS = 120;

function base64ToBlob(base64, mimeType) {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice      = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length).fill(null).map((_, i) => slice.charCodeAt(i));
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays, { type: mimeType });
}

const useCognitive = () => {

  const [isLoading,       setIsLoading]       = useState(false);
  const [isRecording,     setIsRecording]     = useState(false);
  const [isPlaying,       setIsPlaying]       = useState(false);
  const [isPaused,        setIsPaused]        = useState(false);
  const [isMuted,         setIsMuted]         = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [error,           setError]           = useState(null);
  const [recordingTime,   setRecordingTime]   = useState(MAX_RECORDING_SECONDS);
  const [availableMics,   setAvailableMics]   = useState([]);
  const [selectedMicId,   setSelectedMicId]   = useState('default');

  const audioRef         = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const audioStreamRef   = useRef(null);
  const audioBlobRef     = useRef(null);
  const countdownRef     = useRef(null);
  const autoStopRef      = useRef(null);

  const tts_model = 'tts-1';

  useEffect(() => {
    return () => {
      clearInterval(countdownRef.current);
      clearTimeout(autoStopRef.current);
    };
  }, []);

  // ============================================================================
  // MICROPHONE MANAGEMENT
  // ============================================================================

  const loadMicrophones = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics    = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({
          deviceId: d.deviceId,
          label:    d.label || `Microphone ${d.deviceId.slice(0, 6)}`,
        }));

      setAvailableMics(mics);
      return mics;
    } catch (err) {
      console.error('❌ useCognitive.loadMicrophones error:', err);
      setError('Could not access microphone devices.');
      return [];
    }
  }, []);

  // ============================================================================
  // TEXT-TO-SPEECH
  // ============================================================================

  const speakTextAzure = useCallback(async (text, voice, language, volume = 1.0) => {
    if (!text?.trim()) return { success: false, error: 'No text provided' };
    setIsLoading(true);
    setError(null);

    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }

      const response = await cognitiveAPI.textToSpeech(
        text,
        voice    || 'en-US-JennyNeural',
        language || 'en-US'
      );

      if (!response.success || !response.audioData) {
        throw new Error(response.error || 'Failed to generate speech');
      }

      const audioBlob = base64ToBlob(response.audioData, 'audio/mp3');
      const audioURL  = URL.createObjectURL(audioBlob);
      const audio     = new Audio(audioURL);

      audio.volume = Math.max(0, Math.min(1, volume));

      audio.onended = () => {
        setIsPlaying(false);
        setIsPaused(false);
        audioRef.current = null;
        URL.revokeObjectURL(audioURL);
      };

      audio.onerror = () => {
        setIsPlaying(false);
        setIsPaused(false);
        setError('Audio playback failed');
        audioRef.current = null;
        URL.revokeObjectURL(audioURL);
      };

      audioRef.current = audio;

      if (!isMuted) {
        await audio.play();
        setIsPlaying(true);
        setIsPaused(false);
      }

      return { success: true };
    } catch (err) {
      console.error('❌ useCognitive.speakText error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isMuted]);

  const speakText = useCallback(async (text, voice, language, volume = 1.0) => {
    if (!text?.trim()) return { success: false, error: 'No text provided' };
    setIsLoading(true);
    setError(null);

    try {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
        } catch (_) {}
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }

      const response = await cognitiveAPI.textToSpeech(
        text,
        voice  || 'alloy',   // ← OpenAI default
        tts_model  || 'tts-1'    // ← OpenAI tts model
      );

      if (!response.success || !response.audioData) {
        throw new Error(response.error || 'Failed to generate speech');
      }

      const audioBlob = base64ToBlob(response.audioData, 'audio/mp3');
      const audioURL  = URL.createObjectURL(audioBlob);
      const audio     = new Audio(audioURL);

      audio.volume = Math.max(0, Math.min(1, volume));

      audio.onended = () => {
        setIsPlaying(false);
        setIsPaused(false);
        audioRef.current = null;
        URL.revokeObjectURL(audioURL);
      };

      audio.onerror = () => {
        setIsPlaying(false);
        setIsPaused(false);
        setError('Audio playback failed');
        audioRef.current = null;
        URL.revokeObjectURL(audioURL);
      };

      audioRef.current = audio;

      if (!isMuted) {
        try {
          await audio.play();
          setIsPlaying(true);
          setIsPaused(false);
        } catch (playError) {
          if (playError.name === 'AbortError') {
            return { success: true };
          }
          throw playError;
        }
      }

      return { success: true };
    } catch (err) {
      console.error('❌ useCognitive.speakText error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isMuted]);

  const pauseSpeech = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPlaying(false);
      setIsPaused(true);
    }
  }, []);

  const resumeSpeech = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
      setIsPaused(false);
    }
  }, []);

  const stopSpeech = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (_) {}
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const nowMuted = !prev;
      if (nowMuted) pauseSpeech();
      else if (isPaused) resumeSpeech();
      return nowMuted;
    });
  }, [isPaused, pauseSpeech, resumeSpeech]);

  const setVolume = useCallback((volume) => {
    const clamped = Math.max(0, Math.min(1, volume));
    if (audioRef.current) audioRef.current.volume = clamped;
  }, []);

  // ============================================================================
  // SPEECH-TO-TEXT
  // ============================================================================

  const startRecording = useCallback(async () => {
    setError(null);

    try {
      const audioConstraints = selectedMicId && selectedMicId !== 'default'
        ? { deviceId: { exact: selectedMicId } }
        : true;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioBlobRef.current = blob;

        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => track.stop());
          audioStreamRef.current = null;
        }

        clearInterval(countdownRef.current);
        clearTimeout(autoStopRef.current);
        setRecordingTime(MAX_RECORDING_SECONDS);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(MAX_RECORDING_SECONDS);

      // Countdown timer
      let remaining = MAX_RECORDING_SECONDS;
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setRecordingTime(remaining);
      }, 1000);

      // Auto-stop at 120 seconds
      autoStopRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
          setIsRecording(false);
        }
      }, MAX_RECORDING_SECONDS * 1000);

      return { success: true };
    } catch (err) {
      console.error('❌ useCognitive.startRecording error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [selectedMicId]);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
        resolve({ success: false, error: 'Not currently recording' });
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioBlobRef.current = blob;

        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => track.stop());
          audioStreamRef.current = null;
        }

        clearInterval(countdownRef.current);
        clearTimeout(autoStopRef.current);
        setRecordingTime(MAX_RECORDING_SECONDS);
        setIsRecording(false);
        resolve({ success: true });
      };

      mediaRecorderRef.current.stop();
    });
  }, []);

  const transcribeAudio = useCallback(async (audioBlob, language = 'en') => {
    setIsLoading(true);
    setError(null);

    try {
      const blob = audioBlob || audioBlobRef.current;
      if (!blob) throw new Error('No audio to transcribe.');

      const response = await cognitiveAPI.speechToText(blob, language);
      if (!response.success) throw new Error(response.error || 'Transcription failed');

      const text = response.text?.trim() || '';
      setTranscribedText(text);
      return response;
    } catch (err) {
      console.error('❌ useCognitive.transcribeAudio error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearTranscription = useCallback(() => {
    setTranscribedText('');
    audioBlobRef.current = null;
  }, []);

  const playRecordedAudio = useCallback(() => {
    const blob = audioBlobRef.current;
    if (!blob) return;
    const audioURL = URL.createObjectURL(blob);
    const audio    = new Audio(audioURL);
    audio.onended  = () => URL.revokeObjectURL(audioURL);
    audio.play();
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const reset = useCallback(() => {
    stopSpeech();
    if (mediaRecorderRef.current?.state === 'recording') stopRecording();
    clearTranscription();
    clearInterval(countdownRef.current);
    clearTimeout(autoStopRef.current);
    setRecordingTime(MAX_RECORDING_SECONDS);
    setIsLoading(false);
    setError(null);
  }, [stopSpeech, stopRecording, clearTranscription]);

  return {
    isLoading,
    isRecording,
    isPlaying,
    isPaused,
    isMuted,
    transcribedText,
    error,
    recordingTime,
    maxRecordingTime: MAX_RECORDING_SECONDS,
    availableMics,
    selectedMicId,
    setSelectedMicId,
    loadMicrophones,
    speakTextAzure,
    speakText,
    pauseSpeech,
    resumeSpeech,
    stopSpeech,
    toggleMute,
    setVolume,
    startRecording,
    stopRecording,
    transcribeAudio,
    clearTranscription,
    playRecordedAudio,
    clearError,
    reset,
  };
};

export default useCognitive;